/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import PcgPlaybackFilterGraph, {
  DEFAULT_CUSTOM_BAND,
  PcgFilterBand,
  PcgFilterLevelGains,
  PcgFilterPreset,
} from "@medaica/common/components/pcg-viewer/audio/pcg-playback-filter"

/**
 * Structural type of audio-graph.js's createAudioGraph() return value (that
 * module is plain JS). The graph owns the page's ONE AudioContext and the
 * one-shot createMediaElementSource capture; this hook only splices filter
 * nodes into it.
 */
type PcgAudioGraph = {
  context: BaseAudioContext
  source: AudioNode
  output: AudioNode
  resume: () => void
  close: () => void
  spliceFilter: (build: (ctx: BaseAudioContext, source: AudioNode, destination: AudioNode) => unknown) => unknown
  unspliceFilter: () => void
  /** Playback listening gain (0–5, 1 = unity); playback only, never analysis. */
  setAmplification: (gain: number) => void
  /** Per-recording preset level-compensation gains for the spliced filter chain; playback only. */
  setFilterLevelGains: (gains: PcgFilterLevelGains | null) => void
}

type PcgPlaybackFilterControls = {
  preset: PcgFilterPreset
  customBand: PcgFilterBand
  /** null while bypassed or while no viewer audio graph is mounted */
  activeBand: PcgFilterBand | null
  setPreset: (preset: "off" | "wide" | "low" | "high") => void
  setCustomBand: (lowHz: number, highHz: number) => void
  /** false until a viewer audio graph exists (no recording mounted, or no AudioContext) */
  available: boolean
}

/**
 * Wires the PCG listening-filter chain into the PcgRecordingViewer's audio
 * graph (obtained via the viewer's onAudioGraph prop).
 *
 * Ownership: the viewer's graph (audio-graph.js) is the single owner of the
 * AudioContext + createMediaElementSource for its <audio> element; this hook
 * must not create its own (createMediaElementSource is one-shot, so two
 * contexts cannot coexist on one element). The filter chain is spliced
 * between the graph's source and output gain via graph.spliceFilter(). There
 * is no lazy-context optimization to make (the context exists eagerly
 * regardless); "off" is the chain's
 * click-free dry path, so the chain is spliced eagerly whenever a graph
 * mounts and the page-lifetime preset selection is re-applied to each new
 * graph (the viewer remounts its audio element per guide-ready recording
 * selection).
 */
const usePcgPlaybackFilter = (audioGraph: PcgAudioGraph | null): PcgPlaybackFilterControls => {
  const filterRef = useRef<PcgPlaybackFilterGraph | null>(null)
  const [selectedPreset, setSelectedPreset] = useState<PcgFilterPreset>("off")
  const [selectedBand, setSelectedBand] = useState<PcgFilterBand>(DEFAULT_CUSTOM_BAND)
  // Mirrors for the splice effect: applying the current selection to a NEW
  // graph must not re-run the effect on every selection change.
  const presetRef = useRef<PcgFilterPreset>(selectedPreset)
  const customBandRef = useRef<PcgFilterBand>(selectedBand)

  useEffect(() => {
    if (!audioGraph) {
      filterRef.current = null
      return undefined
    }
    const filter = audioGraph.spliceFilter(
      (ctx, source, destination) => new PcgPlaybackFilterGraph(ctx, source, destination)
    ) as PcgPlaybackFilterGraph | null
    filterRef.current = filter
    if (filter) {
      // Preset selection is page-lifetime; re-apply it to the fresh chain.
      if (presetRef.current === "custom") {
        filter.setCustomBand(customBandRef.current[0], customBandRef.current[1])
      } else if (presetRef.current !== "off") {
        filter.setPreset(presetRef.current)
      }
    }
    return () => {
      audioGraph.unspliceFilter()
      filterRef.current = null
    }
  }, [audioGraph])

  const setPreset = useCallback(
    (nextPreset: "off" | "wide" | "low" | "high"): void => {
      presetRef.current = nextPreset
      setSelectedPreset(nextPreset)
      const filter = filterRef.current
      if (filter) {
        if (nextPreset !== "off" && audioGraph) audioGraph.resume()
        filter.setPreset(nextPreset)
      }
    },
    [audioGraph]
  )

  const setCustomBand = useCallback(
    (lowHz: number, highHz: number): void => {
      const filter = filterRef.current
      if (filter) {
        if (audioGraph) audioGraph.resume()
        if (!filter.setCustomBand(lowHz, highHz)) return
      } else if (!(Number.isFinite(lowHz) && Number.isFinite(highHz) && lowHz < highHz)) {
        return
      }
      presetRef.current = "custom"
      customBandRef.current = [lowHz, highHz]
      setSelectedPreset("custom")
      setSelectedBand([lowHz, highHz])
    },
    [audioGraph]
  )

  return {
    preset: selectedPreset,
    customBand: selectedBand,
    activeBand: filterRef.current?.activeBand ?? null,
    setPreset,
    setCustomBand,
    available: Boolean(audioGraph),
  }
}

export default usePcgPlaybackFilter
export type { PcgPlaybackFilterControls, PcgAudioGraph }
